
import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { Filter, Calendar, History, Plus, X } from 'lucide-react'
import { Body, Button, ButtonVariant } from '@meiuca/ui-components'
import { GameSearch } from './GameSearch'
import { GameFiltersModal, type GameFilters } from './GameFiltersModal'
import { FilterChip } from '@meiuca/ui-components'
import { GameItem } from './GameItem'
import { Game, ParticipationStatus } from '@/types/game'
import { CreateGameModal } from './CreateGameModal'
import { CreateGameData } from '@/types/game'
import { SegmentedControl } from './SegmentedControl'
import { gameService, GameStatus } from '@/services/gameService'
import { formatDateForUI } from '@meiuca/web-foundation'

interface GamesListProps {
  onShare?: (game: Game) => void
  onGameClick?: (gameId: string) => void
  onCreateGame?: (gameData: CreateGameData) => Promise<any>
  onSuccess?: (message: string) => void
  onError?: (message: string) => void
  onJoinGame?: (gameId: string) => void
  onConfirmParticipation?: (gameId: string) => void
  onDeclineInvitation?: (gameId: string) => void
  onCancelParticipation?: (gameId: string) => void
}

export function GamesList({ 
  onShare, 
  onGameClick, 
  onCreateGame, 
  onSuccess, 
  onError,
  onJoinGame,
  onConfirmParticipation,
  onDeclineInvitation,
  onCancelParticipation
}: GamesListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<Partial<GameFilters>>({})
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [isCreateGameOpen, setIsCreateGameOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'proximos' | 'passados'>('proximos')
  
  // States para jogos e loading
  const [proximosJogos, setProximosJogos] = useState<Game[]>([])
  const [jogosPassados, setJogosPassados] = useState<Game[]>([])
  const [loadingProximos, setLoadingProximos] = useState(false)
  const [loadingPassados, setLoadingPassados] = useState(false)
  const [proximosLoaded, setProximosLoaded] = useState(false)
  const [passadosLoaded, setPassadosLoaded] = useState(false)
  const [proximosError, setProximosError] = useState<string | null>(null)
  const [passadosError, setPassadosError] = useState<string | null>(null)

  // Flag de controle para prevenir múltiplas chamadas
  const loadingRef = useRef({ proximos: false, passados: false })
  
  // Ref para debounce da busca
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  // Ref para rastrear se o usuário já fez alguma busca
  const hasSearchedRef = useRef(false)

  // Load data when needed - single effect for each tab
  useEffect(() => {
    const loadProximosJogos = async () => {
      if (loadingProximos || loadingRef.current.proximos) return
      
      loadingRef.current.proximos = true
      const callId = Math.random().toString(36).substring(7)
      console.log(`🟢 Loading próximos jogos [${callId}] - SINGLE CALL`)
      
      try {
        setLoadingProximos(true)
        setProximosError(null)
        // Combina filtros com query de busca
        const combinedFilters = {
          ...filters,
          query: searchQuery
        }
        const games = await gameService.getMyGames([GameStatus.ACTIVE], combinedFilters)
        setProximosJogos(games.sort((a, b) => a.startDate.getTime() - b.startDate.getTime()))
        setProximosLoaded(true)
      } catch (error) {
        console.error('❌ Error loading próximos jogos:', error)
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
        setProximosError(errorMessage)
        setProximosLoaded(true)
        onError && onError(`Erro ao carregar jogos: ${errorMessage}`)
      } finally {
        setLoadingProximos(false)
        loadingRef.current.proximos = false
      }
    }

    if (activeTab === 'proximos' && !proximosLoaded && !loadingProximos && !loadingRef.current.proximos) {
      console.log('🎯 Loading próximos jogos from useEffect')
      loadProximosJogos()
    }
  }, [activeTab, proximosLoaded, loadingProximos, filters, searchQuery])

  useEffect(() => {
    const loadJogosPassados = async () => {
      if (loadingPassados || loadingRef.current.passados) return
      
      loadingRef.current.passados = true
      const callId = Math.random().toString(36).substring(7)
      console.log(`🟢 Loading jogos passados [${callId}] - SINGLE CALL`)
      
      try {
        setLoadingPassados(true)
        setPassadosError(null)
        // Combina filtros com query de busca
        const combinedFilters = {
          ...filters,
          query: searchQuery
        }
        const games = await gameService.getMyGames([GameStatus.COMPLETED, GameStatus.CANCELED], combinedFilters)
        setJogosPassados(games.sort((a, b) => b.startDate.getTime() - a.startDate.getTime()))
        setPassadosLoaded(true)
      } catch (error) {
        console.error('❌ Error loading jogos passados:', error)
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
        setPassadosError(errorMessage)
        setPassadosLoaded(true)
        onError && onError(`Erro ao carregar jogos passados: ${errorMessage}`)
      } finally {
        setLoadingPassados(false)
        loadingRef.current.passados = false
      }
    }

    if (activeTab === 'passados' && !passadosLoaded && !loadingPassados && !loadingRef.current.passados) {
      console.log('🎯 Loading jogos passados from useEffect')
      loadJogosPassados()
    }
  }, [activeTab, passadosLoaded, loadingPassados, filters, searchQuery])

  // Smart reload: filters apply immediately, search has debounce
  useEffect(() => {
    // Check if filters have meaningful values
    const hasActiveFilters = (
      (filters.modalidade && filters.modalidade.length > 0) ||
      (filters.nivel && filters.nivel.length > 0) ||
      filters.dataPreset ||
      filters.dataCustomizada ||
      (filters.ordenacao && filters.ordenacao !== 'data_asc')
    )
    
    // Marca que o usuário fez uma busca se há query ou filtros
    if (searchQuery || hasActiveFilters) {
      hasSearchedRef.current = true
    }
    
    // Skip if no meaningful filters applied AND no search query AND user never searched (avoids initial run with empty object)
    if (!hasActiveFilters && !searchQuery && !hasSearchedRef.current) return
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    console.log('🔄 Filters/search changed, scheduling reload...', filters, searchQuery)
    
    const reloadData = () => {
      // Reset cache and reload current tab data
      if (activeTab === 'proximos') {
        setProximosLoaded(false)
        setProximosJogos([])
        setProximosError(null)
        loadingRef.current.proximos = false
      } else if (activeTab === 'passados') {
        setPassadosLoaded(false) 
        setJogosPassados([])
        setPassadosError(null)
        loadingRef.current.passados = false
      }
    }
    
    // Smart debounce: 500ms for search, immediate for filters
    const hasSearchQuery = !!searchQuery
    const delayTime = hasSearchQuery ? 500 : 0
    
    searchTimeoutRef.current = setTimeout(() => {
      reloadData()
    }, delayTime)
    
    // Cleanup timeout on unmount or dependency change
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [filters, searchQuery, activeTab])

  // Todos os filtros (incluindo busca) são aplicados na API
  const filteredGames = activeTab === 'proximos' ? proximosJogos : jogosPassados

  const handleApplyFilters = (newFilters: GameFilters) => {
    setFilters(newFilters)
  }

  const getActiveCount = () => {
    return activeTab === 'proximos' ? proximosJogos.length : jogosPassados.length
  }

  const isLoading = () => {
    return activeTab === 'proximos' ? loadingProximos : loadingPassados
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.modalidade && filters.modalidade.length > 0) count++
    if (filters.dataPreset || filters.dataCustomizada) count++
    if (filters.nivel && filters.nivel.length > 0) count++
    if (filters.ordenacao && filters.ordenacao !== 'data_asc') count++
    return count
  }

  const clearFilter = (type: string, value?: string) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      
      if (type === 'modalidade' && value) {
        newFilters.modalidade = prev.modalidade?.filter(m => m !== value) || []
      } else if (type === 'modalidade') {
        newFilters.modalidade = []
      } else if (type === 'nivel' && value) {
        newFilters.nivel = prev.nivel?.filter(n => n !== value) || []
      } else if (type === 'nivel') {
        newFilters.nivel = []
      } else if (type === 'data') {
        newFilters.dataPreset = ''
        newFilters.dataCustomizada = ''
      } else if (type === 'ordenacao') {
        newFilters.ordenacao = 'data_asc'
      }
      
      return newFilters
    })
  }

  const handleCreateGame = async (gameData: CreateGameData) => {
    // Deixa o modal controlar o fechamento baseado no sucesso/erro
    return onCreateGame?.(gameData)
  }

  const reloadCurrentTab = () => {
    console.log('🔄 Reloading current tab data after action')
    if (activeTab === 'proximos') {
      setProximosLoaded(false)
      setProximosJogos([])
      setProximosError(null)
      loadingRef.current.proximos = false
    } else {
      setPassadosLoaded(false)
      setJogosPassados([])
      setPassadosError(null)
      loadingRef.current.passados = false
    }
  }

  // Atualiza o status de um jogo específico na lista local
  const updateGameStatus = (gameId: string, newStatus: ParticipationStatus) => {
    const updateGames = (games: Game[]) => 
      games.map(game => 
        game.id === gameId 
          ? { ...game, userStatus: newStatus }
          : game
      )

    if (activeTab === 'proximos') {
      setProximosJogos(prev => updateGames(prev))
    } else {
      setJogosPassados(prev => updateGames(prev))
    }
  }

  // Wrapper handlers que atualizam UI imediatamente e fazem reload
  const handleJoinGameWithReload = async (gameId: string) => {
    await onJoinGame?.(gameId)
    reloadCurrentTab()
  }

  const handleConfirmParticipationWithReload = async (gameId: string) => {
    await onConfirmParticipation?.(gameId)
    updateGameStatus(gameId, ParticipationStatus.CONFIRMED)
    // Reload após um delay para garantir consistência
    setTimeout(() => reloadCurrentTab(), 1000)
  }

  const handleDeclineInvitationWithReload = async (gameId: string) => {
    await onDeclineInvitation?.(gameId)
    updateGameStatus(gameId, ParticipationStatus.DECLINED)
    setTimeout(() => reloadCurrentTab(), 1000)
  }

  const handleCancelParticipationWithReload = async (gameId: string) => {
    await onCancelParticipation?.(gameId)
    updateGameStatus(gameId, ParticipationStatus.CANCELED)
    setTimeout(() => reloadCurrentTab(), 1000)
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex gap-2">
        <div className="flex-1">
          <GameSearch onSearch={setSearchQuery} />
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Button
              buttonStyle={getActiveFiltersCount() > 0 ? "filled" : "bordered"}
              variant={getActiveFiltersCount() > 0 ? ButtonVariant.PRIMARY : undefined}
              size="sm"
              onClick={() => setIsFiltersOpen(true)}
              className="p-2"
              title="Filtros"
            >
              <Filter className="w-5 h-5" />
            </Button>
            {getActiveFiltersCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-status-error-text text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {getActiveFiltersCount()}
              </span>
            )}
          </div>
          
          {onCreateGame && (
            <Button
              variant={ButtonVariant.PRIMARY}
              size="sm"
              onClick={() => setIsCreateGameOpen(true)}
              className="p-2"
              title="Criar Jogo"
            >
              <Plus className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Tags */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex flex-wrap gap-2">
          {/* Modalidade Tags */}
          {filters.modalidade?.map((modalidade: string) => (
            <FilterChip
              key={modalidade}
              label={modalidade}
              onRemove={() => clearFilter('modalidade', modalidade)}
              variant="secondary"
            />
          ))}
          
          {/* Nível Tags */}
          {filters.nivel?.map((nivel: string) => (
            <FilterChip
              key={nivel}
              label={nivel}
              onRemove={() => clearFilter('nivel', nivel)}
              variant="secondary"
            />
          ))}
          
          {/* Data Tag */}
          {(filters.dataPreset || filters.dataCustomizada) && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
              <span>
                {filters.dataPreset === 'hoje' && 'Hoje'}
                {filters.dataPreset === 'amanha' && 'Amanhã'}
                {filters.dataPreset === 'esta-semana' && 'Esta Semana'}
                {filters.dataPreset === 'customizada' && filters.dataCustomizada && 
                  formatDateForUI(filters.dataCustomizada)}
              </span>
              <button 
                onClick={() => clearFilter('data')}
                className="hover:bg-green-200 rounded-full p-0.5"
              >
                ×
              </button>
            </div>
          )}
          
          {/* Ordenação Tag */}
          {filters.ordenacao && filters.ordenacao !== 'data_asc' && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
              <span>Data (Decrescente)</span>
              <button 
                onClick={() => clearFilter('ordenacao')}
                className="hover:bg-purple-200 rounded-full p-0.5"
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <SegmentedControl
        options={[
          {
            id: 'proximos',
            label: 'Próximos',
            icon: Calendar
          },
          {
            id: 'passados',
            label: 'Anteriores', 
            icon: History
          }
        ]}
        activeOption={activeTab}
        onChange={(tab) => setActiveTab(tab as 'proximos' | 'passados')}
      />


      {/* Games List */}
      <div className="space-y-4">
        {isLoading() ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center py-12"
          >
            <div className="text-text-secondary">
              Carregando {activeTab === 'proximos' ? 'próximos jogos' : 'jogos anteriores'}...
            </div>
          </motion.div>
        ) : (activeTab === 'proximos' ? proximosError : passadosError) ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-status-error-bg rounded-xl border border-status-error-border"
          >
            <div className="w-16 h-16 bg-status-error-bg rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-status-error-text" />
            </div>
            <h2 className="text-lg font-semibold text-status-error-text mb-2">
              Erro ao carregar jogos
            </h2>
            <Body className="text-status-error-text mb-4">
              {activeTab === 'proximos' ? proximosError : passadosError}
            </Body>
            <Button
              variant={ButtonVariant.PRIMARY}
              size="sm"
              onClick={() => reloadCurrentTab()}
            >
              Tentar novamente
            </Button>
          </motion.div>
        ) : filteredGames.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-background-white rounded-xl border border-border-primary"
          >
            <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              {activeTab === 'proximos' ? (
                <Calendar className="w-8 h-8 text-text-secondary" />
              ) : (
                <History className="w-8 h-8 text-text-secondary" />
              )}
            </div>
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              {searchQuery || Object.keys(filters).length > 0
                ? 'Nenhum jogo encontrado'
                : activeTab === 'proximos'
                  ? 'Nenhum jogo próximo'
                  : 'Nenhum jogo anterior'
              }
            </h2>
            <Body className="text-text-secondary">
              {searchQuery || Object.keys(filters).length > 0
                ? 'Tente ajustar sua busca ou filtros'
                : activeTab === 'proximos'
                  ? 'Você não tem jogos agendados no momento'
                  : 'Você ainda não participou de nenhum jogo'
              }
            </Body>
          </motion.div>
        ) : (
          filteredGames.map((game, index) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <GameItem 
                game={game} 
                onShare={onShare} 
                onGameClick={onGameClick}
                onJoinGame={handleJoinGameWithReload}
                onConfirmParticipation={handleConfirmParticipationWithReload}
                onDeclineInvitation={handleDeclineInvitationWithReload}
                onCancelParticipation={handleCancelParticipationWithReload}
              />
            </motion.div>
          ))
        )}
      </div>

      {/* Filters Modal */}
      <GameFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />

      {/* Create Game Modal */}
      <CreateGameModal
        isOpen={isCreateGameOpen}
        onClose={() => setIsCreateGameOpen(false)}
        onCreateGame={handleCreateGame}
        onSuccess={onSuccess}
        onError={onError}
      />
    </div>
  )
}